import type { ApolloClient } from "@apollo/client";
import { merge } from "es-toolkit/compat";
import { UPDATE_USER } from "api/mutations";
import { reporterForStatus } from "api/reporterForStatus";
import { readUser } from "api/user";
import { FIELD_OWNERS } from "graphql/fieldOwners.generated";
import type {
  UpdateUserMutation,
  UpdateUserMutationVariables,
} from "graphql/generated";
import {
  LOCAL_STORAGE_USER,
  storeDirectlyIntoLocalStorage,
  UI_STATE,
} from "utils/localStorage";

/**
 * The free-form bag of UI preferences. The server keeps it as a JSON string,
 * so the keys and shapes are not known at compile time.
 */
export type UiState = { [key: string]: any };

// `getUser.uiState` is a JSON string. A value that is not a JSON object gives
// an empty state.
export const parseUiState = (
  rawUiState: string | null | undefined,
): UiState => {
  if (!rawUiState) return {};
  try {
    const uiState = JSON.parse(rawUiState);
    return typeof uiState === "object" && uiState !== null ? uiState : {};
  } catch {
    return {};
  }
};

// The saved UI state of the signed-in user, from the client's cached
// `GET_USER`.
export const readUiState = (client: ApolloClient<object>) =>
  parseUiState(readUser(client)?.uiState);

/**
 * Saves `uiState` with `UPDATE_USER` and returns true when the server keeps
 * it. The response updates the cached `User`. The function reports failures
 * and does not throw.
 *
 * Give `userId` when the write belongs to a session. A logout or another login
 * can occur while the mutation runs. Then the response is not for the current
 * user, so the function does not copy it to localStorage or report its errors.
 * Apollo still writes it to the cache.
 */
export const saveUiState = async (
  client: ApolloClient<object>,
  uiState: UiState,
  userId?: string,
) => {
  try {
    const { data, errors } = await client.mutate<
      UpdateUserMutation,
      UpdateUserMutationVariables
    >({
      mutation: UPDATE_USER,
      variables: { uiState: JSON.stringify(uiState) },
    });
    if (userId && readUser(client)?.id !== userId) {
      return false;
    }
    if (data) {
      // The router reads this copy before `GET_USER` is in the cache.
      storeDirectlyIntoLocalStorage(UI_STATE, uiState, LOCAL_STORAGE_USER);
    }
    if (errors) {
      const statusCode = errors[0]?.extensions?.status as number | undefined;

      // Per-status fingerprint keeps 401 logout-race noise and real 5xx
      // faults in separate issues.
      reporterForStatus(statusCode)("UPDATE USER FAILED", {
        source: "client",
        team: FIELD_OWNERS.updateUser,
        uiState,
        errors,
        fingerprint: ["update-user-failed", String(statusCode ?? "unknown")],
        ...(statusCode ? { statusCode } : {}),
      });
    }
    return !!data;
  } catch (e) {
    console.error(e);
    return false;
  }
};

/**
 * Merges `newUiState` into the saved UI state of the signed-in user and saves
 * the result. Without a signed-in user, it does nothing.
 */
export const mergeUiState = async (
  client: ApolloClient<object>,
  newUiState: UiState,
) => {
  const userId = readUser(client)?.id;
  if (!userId) return;
  await saveUiState(client, merge({}, readUiState(client), newUiState), userId);
};
